import { NotificationType } from '@naturehouse/design-system/components/atoms/notification/Notification';
import ExtraCostsListModel from '../../../models/extra-costs-list/ExtraCostsListModel';
import type {
    PriceListCollectionApiData,
    PriceListPriceInformation
} from '../../../collections/price-list/PriceListCollection.types';
import HousePriceListCollection from '../../../collections/price-list/HousePriceListCollection';
import createNotification from '../../../util/notification';
import TranslationManager from '../../../common/TranslationManager';
import ExtraCostsListRenderer from '../../app/ExtraCostsListRenderer';

type SearchResultsExtraCostsDialogTranslations = {
    errorRetrievingPriceStructure: string;
};

export class SearchResultsExtraCostsDialogRenderer {
    static #instance: SearchResultsExtraCostsDialogRenderer | null = null;

    readonly #form: HTMLFormElement | null = null;

    readonly #priceListCollection: HousePriceListCollection =
        HousePriceListCollection.getInstance();

    #translations: SearchResultsExtraCostsDialogTranslations | null = null;

    constructor() {
        this.#form = document.getElementById('search-form') as HTMLFormElement;
    }

    public static getInstance(): SearchResultsExtraCostsDialogRenderer {
        if (SearchResultsExtraCostsDialogRenderer.#instance === null) {
            SearchResultsExtraCostsDialogRenderer.#instance =
                new SearchResultsExtraCostsDialogRenderer();
        }
        return this.#instance!;
    }

    public static destroyInstance(): void {
        this.#instance = null;
    }

    public async render(
        toggleDialogButton: HTMLButtonElement,
        clickedHouseId: string
    ): Promise<string | null> {
        const { infoIcon, spinnerIcon } = this.#getLoadingElements(toggleDialogButton);
        this.#handleToggleLoadingState({ infoIcon, spinnerIcon, loading: true });

        const data = await this.#loadDialogData(toggleDialogButton, clickedHouseId);
        this.#handleToggleLoadingState({ infoIcon, spinnerIcon, loading: false });

        if (!data?.priceInformation) {
            return null;
        }
        return this.#renderDialogContent(data.priceInformation);
    }

    readonly #loadDialogData = async (
        currentTarget: HTMLButtonElement,
        clickedHouseId: string
    ): Promise<PriceListCollectionApiData | null> => {
        const { infoIcon, spinnerIcon } = this.#getLoadingElements(currentTarget);
        this.#handleToggleLoadingState({ infoIcon, spinnerIcon, loading: true });

        const urlSearchParams = this.#getDialogSearchParams(currentTarget);
        const data = await this.#fetchPriceListData(clickedHouseId, urlSearchParams);
        if (!data?.priceInformation) {
            await this.#handleDialogError(infoIcon, spinnerIcon);
            return null;
        }

        return data;
    };

    readonly #getLoadingElements = (
        currentTarget: HTMLButtonElement
    ): { infoIcon: Element | null; spinnerIcon: Element | null } => ({
        infoIcon: currentTarget.querySelector('[data-role="extra-costs-dialog-toggle-icon"]'),
        spinnerIcon: currentTarget.querySelector('[data-role="extra-costs-dialog-toggle-spinner"]')
    });

    readonly #getDialogSearchParams = (currentTarget: HTMLButtonElement): URLSearchParams => {
        const formData = new FormData(this.#form!);
        formData.set('arrivalDate', currentTarget.dataset.arrivalDate!);
        formData.set('departureDate', currentTarget.dataset.departureDate!);
        return ExtraCostsListModel.parseToSearchParams(formData);
    };

    readonly #handleDialogError = async (
        infoIcon: Element | null,
        spinnerIcon: Element | null
    ): Promise<void> => {
        await this.#handleShowError();
        this.#handleToggleLoadingState({ infoIcon, spinnerIcon, loading: false });
    };

    async #fetchPriceListData(
        houseId: string,
        searchParams: URLSearchParams
    ): Promise<void | PriceListCollectionApiData> {
        return this.#priceListCollection.retrieve<PriceListCollectionApiData>({
            houseId,
            searchParams
        });
    }

    async #renderDialogContent(priceInformation: PriceListPriceInformation): Promise<string> {
        const props = await ExtraCostsListModel.parseToTemplateParams(priceInformation);
        return ExtraCostsListRenderer.render(props);
    }

    #handleToggleLoadingState({
        infoIcon,
        spinnerIcon,
        loading
    }: {
        infoIcon: Element | null;
        spinnerIcon?: Element | null;
        loading: boolean;
    }): void {
        if (!infoIcon || !spinnerIcon) {
            return;
        }

        infoIcon.classList.toggle('hidden', loading);
        spinnerIcon.classList.toggle('hidden', !loading);
    }

    async #handleShowError(): Promise<void> {
        await this.#getTranslations();

        createNotification(
            NotificationType.ERROR,
            this.#translations!.errorRetrievingPriceStructure,
            0
        );
    }

    async #getTranslations(): Promise<void> {
        if (this.#translations) {
            return;
        }
        const translationManager = await TranslationManager.getInstance();
        this.#translations = {
            errorRetrievingPriceStructure: translationManager.translate(
                'error_retrieving_price_structure.title'
            )
        };
    }
}

export default SearchResultsExtraCostsDialogRenderer;
